import { Modal, ModalCloseButton, ModalContent, ModalOverlay } from '@chakra-ui/react';
import { GanttProps, Task as GanttTaskProps } from '@sketchnote/gantt-chart';
import { ViewMode } from '@sketchnote/gantt-chart';
import { formatISO } from 'date-fns';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import * as React from 'react';
import { Node } from 'slate';
import { useBoardStore } from 'src/features/boards';
import { BoardStoreState } from 'src/features/boards/store/useBoardStore';
import { useKanbanStore } from 'src/features/kanban';
import { calculatePercentage } from 'src/features/kanban/component/Item';
import { TaskEdit } from 'src/features/tasks';
import { getFormattedValue } from 'src/utils/getFormattedValue';
import { shallow } from 'zustand/shallow';

import { Column } from '../../kanban/component/Column/types';
import { KanbanState } from '../../kanban/store/types';
import { TaskListHeader } from './TaskListHeader';
import { TaskListTable } from './TaskListTable';
import { TaskTooltip } from './TaskTooltip';

const { useState, useEffect } = React;

const Gantt = dynamic<GanttProps>(() => import('@sketchnote/gantt-chart').then((s) => s.Gantt), {
  ssr: false,
});

export const GanttView = () => {
  const [tasks, setTasks] = useState<GanttTaskProps[]>([]);
  const {
    columns,
    setItem,
    onUpdateItemField,
    currentSelectedItem,
    onDeleteItem,
    onDuplicateItem,
    onChangeCurrentSelectedItem,
    onCloseCurrentSelectedItem,
  } = useKanbanStore(boardSelector, shallow);
  const boardsStore = useBoardStore();
  const [isDeletePromptOpen, setIsDeletePromptOpen] = useState<boolean>(false);
  const [isDuplicatePromptOpen, setIsDuplicatePromptOpen] = useState<boolean>(false);
  const router = useRouter();
  const orgId = router?.query?.orgId;
  const mode = router?.query?.mode;
  useEffect(() => {
    setTasks(getGanttTasksForColumns(columns, boardsStore.ganttTaskColorMode));
  }, [columns, boardsStore.ganttTaskColorMode]);

  useEffect(() => {
    if (router?.query?.taskId) {
      const foundTask = columns
        .reduce((acc, column) => {
          return [...acc, ...column.items];
        }, [])
        .find((item) => item.id === router.query.taskId);
      if (foundTask) {
        onChangeCurrentSelectedItem({
          isOpen: true,
          columnId: foundTask.column,
          mode: 'edit',
          item: foundTask,
        });
      }
    }
  }, [router?.query?.taskId]);

  useEffect(() => {
    if (!router?.query?.taskId) {
      onCloseCurrentSelectedItem();
    }
  }, [router]);

  const handleTaskChange = (task: GanttTaskProps) => {
    let newTasks = tasks.map((t) => (t.id === task.id ? task : t));
    if (task.project) {
      const dates = getStartEndDateForProject(newTasks, task.project);
      if (dates) {
        const [start, end] = dates;
        const project = newTasks[newTasks.findIndex((t) => t.id === task.project)];
        if (
          project.start.getTime() !== start.getTime() ||
          project.end.getTime() !== end.getTime()
        ) {
          const changedProject = { ...project, start, end };
          newTasks = newTasks.map((t) => (t.id === task.project ? changedProject : t));
        }
      }
    }
    onUpdateItemField({
      item: { assignDate: [formatISO(task.start), formatISO(task.end)], id: task.id },
      keys: ['assignDate'],
      columnId: task.data?.column,
    });
    setTasks(newTasks);
  };

  const handleExpanderClick = (task: GanttTaskProps) => {
    setTasks(tasks.map((t) => (t.id === task.id ? task : t)));
  };

  const handleTaskViewClose = () => {
    router.push(
      boardsStore.isPublicView
        ? `/${mode}/${orgId}/files/${router.query.slug}/public?view=${router.query.view}&type=${router.query.type}`
        : `/${mode}/${orgId}/files/${router.query.slug}?view=${router.query.view}&type=${router.query.type}`
    );
  };

  const onToggleDeletePrompt =
    (isOpen = true) =>
    () => {
      setIsDeletePromptOpen(isOpen);
    };

  const onToggleDuplicatePrompt =
    (isOpen = true) =>
    () => {
      setIsDuplicatePromptOpen(isOpen);
    };

  const handleDeleteTask = () => {
    if (currentSelectedItem.item?.id && currentSelectedItem.columnId) {
      onDeleteItem({
        columnId: currentSelectedItem.columnId,
        itemId: currentSelectedItem.item.id,
      });
      setIsDeletePromptOpen(false);
      router.push(
        `/${mode}/${orgId}/files/${router.query.slug}?view=${router.query.view}&type=${router.query.type}`
      );
    }
  };

  const handleDuplicateTask = () => {
    if (currentSelectedItem.item?.id && currentSelectedItem.columnId) {
      onDuplicateItem({
        columnId: currentSelectedItem.columnId,
        itemId: currentSelectedItem.item.id,
      });
      setIsDuplicatePromptOpen(false);
      router.push(
        `/${mode}/${orgId}/files/${router.query.slug}?view=${router.query.view}&type=${router.query.type}`
      );
    }
  };

  return (
    <div className="bg-gray-100">
      {tasks.length > 0 && (
        <Gantt
          tasks={tasks}
          rowHeight={75}
          viewMode={boardsStore.ganttView}
          TaskListTable={TaskListTable}
          TaskListHeader={TaskListHeader}
          TooltipContent={TaskTooltip}
          onExpanderClick={handleExpanderClick}
          onDoubleClick={(e) => {
            router.push(
              `/${mode}/${orgId}/files/${router.query.slug}?view=${router.query.view}&type=${router.query.type}&taskId=${e.id}`
            );
          }}
          todayColor="#254DDA"
          onDateChange={handleTaskChange}
          barFill={45}
          columnWidth={boardsStore.ganttView === ViewMode.Month ? 80 : 60}
        />
      )}

      <Modal isOpen={!!router.query.taskId} onClose={handleTaskViewClose} motionPreset="none">
        <ModalOverlay />
        <ModalContent
          style={{
            position: 'initial',
            maxWidth: '120rem',
          }}
          className={`min-w-768 h-vh90 bg-white rounded-lg static`}
        >
          {router.query.taskId && (
            <TaskEdit
              id={router?.query?.taskId as string}
              onDelete={onToggleDeletePrompt(true)}
              onDuplicate={onToggleDuplicatePrompt(true)}
              onClose={handleTaskViewClose}
              onChangeTask={(e) => {
                if (!e.task.isSubtask && e.task.column && e.task.id) {
                  setItem({
                    columnId: e.task.column,
                    item: e.task as any,
                    itemId: e.task.id,
                  });
                }
              }}
            />
          )}
        </ModalContent>
      </Modal>
      <Modal
        isOpen={isDeletePromptOpen}
        onClose={() => {
          setIsDeletePromptOpen(false);
        }}
      >
        <ModalOverlay />
        <ModalContent
          style={{
            maxWidth: '46rem',
            padding: '32px',
            marginTop: '20rem',
            borderRadius: '8px',
          }}
        >
          <ModalCloseButton />
          <p className="mb-32 text-md text-gray-999">Are you sure you wish to proceed?</p>
          <div className="flex justify-end">
            <button
              onClick={() => {
                setIsDeletePromptOpen(false);
              }}
              className="border-blue-500 text-blue-500 border rounded-sm px-24 py-13 mr-8"
            >
              Cancel
            </button>
            <button
              className="border-red-700 text-red-700 border rounded-sm px-24 py-13"
              onClick={handleDeleteTask}
            >
              Delete
            </button>
          </div>
        </ModalContent>
      </Modal>
      <Modal
        isOpen={isDuplicatePromptOpen}
        onClose={() => {
          setIsDuplicatePromptOpen(false);
        }}
      >
        <ModalOverlay />

        <ModalContent
          style={{
            maxWidth: '46rem',
            padding: '32px',
            marginTop: '20rem',
            borderRadius: '8px',
          }}
        >
          <ModalCloseButton />
          <p className="mb-32 text-md text-gray-999">Are you sure you wish to proceed?</p>
          <div className="flex justify-end">
            <button
              onClick={() => {
                setIsDuplicatePromptOpen(false);
              }}
              className="border-blue-500 text-blue-500 border rounded-sm px-24 py-13 mr-8"
            >
              Cancel
            </button>
            <button
              className="border-red-700 text-red-700 border rounded-sm px-24 py-13"
              onClick={handleDuplicateTask}
            >
              Duplicate
            </button>
          </div>
        </ModalContent>
      </Modal>
    </div>
  );
};

const getGanttTasksForColumns = (
  columns: Column[],
  colorMode: BoardStoreState['ganttTaskColorMode']
) => {
  const currentDate = new Date();

  const tasks: GanttTaskProps[] = [];
  columns.map((column) => {
    const project: GanttTaskProps = {
      id: column.id,
      name: column.name,
      type: 'project',
      start: new Date(currentDate.getFullYear(), currentDate.getMonth(), 1),
      end: new Date(currentDate.getFullYear(), currentDate.getMonth(), 15),
      progress: 0,
      hideChildren: false,
      data: {
        unscheduled: [],
      },
      styles: {
        backgroundColor: column.color,
        opacity: 1,
      },
    };
    tasks.push(project);
    column.items.map((task) => {
      const newTask: GanttTaskProps = {
        id: task.id || '',
        name: getFormattedValue(task.name) || 'Untitled',
        type: 'task',
        start: new Date(),
        end: new Date(),
        progress: calculatePercentage(task?.subtaskCount || 0, task.completedSubTaskTotal || 0),
        project: column.id,
        styles: {
          backgroundColor:
            colorMode === 'status'
              ? column.color
              : colorMode === 'priority'
              ? task.priority
                ? taskPriorityColor[task.priority]
                : '#F9FAFB'
              : '#F9FAFB',
          opacity: task.completed ? 0.5 : 1,
        },
        data: {
          ...task,
        },
      };
      if (task.assignDate?.length === 2) {
        tasks.push({
          ...newTask,
          start: new Date(task.assignDate[0]),
          end: new Date(task.assignDate[1]),
        });
      } else {
        project.data?.unscheduled.push(newTask);
      }
    });
  });
  tasks.map((task) => {
    if (task.type === 'project') {
      const dates = getStartEndDateForProject(tasks, task.id);
      if (dates) {
        const [start, end] = dates;
        const changedProject = { ...task, start, end };
        tasks.splice(
          tasks.findIndex((t) => t.id === task.id),
          1,
          changedProject
        );
      }
    }
  });
  return tasks;
};

const boardSelector = (s: KanbanState) => ({
  columns: s.columns,
  onAddNewColumn: s.onAddNewColumn,
  currentSelectedItem: s.currentSelectedItem,
  onCloseCurrentSelectedItem: s.onCloseCurrentSelectedItem,
  isReadOnly: s.isReadOnly,
  onUpdateItemPlacement: s.onUpdateItemPlacement,
  onChangeColumnOrder: s.onChangeColumnOrder,
  setItem: s.setItem,
  onDeleteItem: s.onDeleteItem,
  onDuplicateItem: s.onDuplicateItem,
  onChangeCurrentSelectedItem: s.onChangeCurrentSelectedItem,
  onUpdateItemField: s.onUpdateItemField,
});

function getStartEndDateForProject(tasks: GanttTaskProps[], projectId: string) {
  const projectTasks = tasks.filter((t) => t.project === projectId);
  if (projectTasks.length === 0) return;
  let start = projectTasks[0].start;
  let end = projectTasks[0].end;

  for (let i = 0; i < projectTasks.length; i++) {
    const task = projectTasks[i];
    if (start.getTime() > task.start.getTime()) {
      start = task.start;
    }
    if (end.getTime() < task.end.getTime()) {
      end = task.end;
    }
  }
  return [start, end];
}

const taskPriorityColor = {
  low: '#B0EEBB',
  medium: '#FFF0B3',
  high: '#FCCFCA',
};
